import { useState } from "react";
import { ArrowDown, ArrowUp, Braces, ChevronDown, Plus, SlidersHorizontal, Trash2, X } from "lucide-react";
import {
  ALIGN_CHOICES, BLOCK_KINDS, OPTION_DEFS, TABLE_LIMITS, TEXT_BLOCKS, WATERMARK_CHOICES, canInsertVariable, getField, groupVariables, optionsOf, setField,
} from "../../lib/records";
import { ClauseCard } from "./ClauseBits";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { ResponsivePopover, ResponsivePopoverContent, ResponsivePopoverTrigger } from "../ui/responsive-popover";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuSeparator as DropdownMenuSeparator, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../ui/responsive-dropdown-menu";

const PLACEHOLDERS = { title: "Titre du document", subtitle: "Sous-titre (ex. les parties)", heading: "Titre de section",
  article: "Intitulé de l'article (facultatif)", paragraph: "Texte du paragraphe…" };

// A choice among a few closed values, shown as a row of buttons (the value is never a typed text — D-82).
export function Segmented({ value, choices, onChange, label, testid }) {
  return (
    <div role="group" aria-label={label} className="inline-flex flex-wrap gap-1" data-testid={testid}>
      {choices.map(([choice, text]) => (
        <button key={String(choice)} type="button" aria-pressed={value === choice} onClick={() => onChange(choice)}
          data-testid={testid ? `${testid}-${choice}` : undefined}
          className={`h-8 rounded-md border px-2.5 text-xs font-medium ${value === choice ? "border-jamora bg-jamora text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
          {text}
        </button>
      ))}
    </div>
  );
}

// « Options » of a block: its closed choices, in a popover on a computer and a bottom panel on a phone.
export function OptionsButton({ block, index, onChange }) {
  const defs = OPTION_DEFS[block.kind];
  if (!defs) return null;
  const options = optionsOf(block);
  return (
    <ResponsivePopover>
      <ResponsivePopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-slate-600" aria-label={`Options du bloc ${index + 1}`} data-testid={`template-block-options-${index}`}>
          <SlidersHorizontal size={14} />Options
        </Button>
      </ResponsivePopoverTrigger>
      <ResponsivePopoverContent title={`Options · ${BLOCK_KINDS[block.kind]}`} align="end" className="w-80 space-y-3 p-3">
        {defs.map((d) => (
          <div key={d.name} className="space-y-1.5">
            <p className="text-xs font-medium text-slate-600">{d.label}</p>
            <Segmented value={options[d.name]} choices={d.choices} label={d.label} testid={`template-option-${index}-${d.name}`}
              onChange={(value) => onChange({ ...block, options: { ...options, [d.name]: value } })} />
          </div>
        ))}
      </ResponsivePopoverContent>
    </ResponsivePopover>
  );
}

// One text field of a block. It remembers its place (block, path, caret) so « Insérer une variable » lands where the person was typing.
function Field({ block, index, path, onChange, onCaret, multiline = false, rows = 3, placeholder, label, className = "", testid, max = 6000 }) {
  const remember = (e) => onCaret({ index, path, start: e.target.selectionStart, end: e.target.selectionEnd });
  const props = {
    value: getField(block, path), maxLength: max, placeholder, "aria-label": label, "data-testid": testid,
    onChange: (e) => { onChange(setField(block, path, e.target.value)); remember(e); }, onSelect: remember, onFocus: remember,
  };
  return multiline ? <Textarea rows={rows} className={className} {...props} /> : <Input className={`h-10 ${className}`} {...props} />;
}

function TableEditor({ block, index, onChange, onCaret }) {
  const columns = block.columns || [];
  const rows = block.rows || [];
  const cell = (r, c) => (
    <Field key={`${r}-${c}`} block={block} index={index} path={["rows", r, c]} onChange={onChange} onCaret={onCaret} max={500}
      label={`Ligne ${r + 1}, colonne ${c + 1}`} className="h-9 min-w-[7.5rem]" testid={`template-table-${index}-cell-${r}-${c}`} />
  );
  const addColumn = () => onChange({ ...block, columns: [...columns, { label: "", align: "left" }], rows: rows.map((r) => [...r, ""]) });
  const removeColumn = (c) => onChange({ ...block, columns: columns.filter((_, i) => i !== c), rows: rows.map((r) => r.filter((_, i) => i !== c)) });
  const setAlign = (c, align) => onChange({ ...block, columns: columns.map((col, i) => (i === c ? { ...col, align } : col)) });
  return (
    <div className="space-y-2" data-testid={`template-table-${index}`}>
      <div className="overflow-x-auto pb-1">
        <table className="w-full border-separate border-spacing-1">
          <thead>
            <tr>
              {columns.map((col, c) => (
                <th key={c} className="min-w-[7.5rem] align-top text-left font-normal">
                  <div className="space-y-1">
                    <Field block={block} index={index} path={["columns", c, "label"]} onChange={onChange} onCaret={onCaret} max={200}
                      label={`Titre de la colonne ${c + 1}`} placeholder={`Colonne ${c + 1}`} className="h-9 font-medium" testid={`template-table-${index}-head-${c}`} />
                    <div className="flex items-center gap-1">
                      <select value={col.align || "left"} onChange={(e) => setAlign(c, e.target.value)} aria-label={`Alignement de la colonne ${c + 1}`}
                        className="h-7 flex-1 rounded border border-slate-200 bg-white px-1 text-xs text-slate-600" data-testid={`template-table-${index}-align-${c}`}>
                        {ALIGN_CHOICES.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
                      </select>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-red-600" aria-label={`Retirer la colonne ${c + 1}`}
                        disabled={columns.length <= 1} onClick={() => removeColumn(c)}><X size={13} /></Button>
                    </div>
                  </div>
                </th>
              ))}
              <th className="w-9" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                {columns.map((_, c) => <td key={c}>{cell(r, c)}</td>)}
                <td>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-red-600" aria-label={`Retirer la ligne ${r + 1}`}
                    onClick={() => onChange({ ...block, rows: rows.filter((_, i) => i !== r) })}><Trash2 size={13} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" disabled={rows.length >= TABLE_LIMITS.rows} data-testid={`template-table-${index}-add-row`}
          onClick={() => onChange({ ...block, rows: [...rows, columns.map(() => "")] })}><Plus size={13} className="mr-1" />Ligne</Button>
        <Button variant="outline" size="sm" disabled={columns.length >= TABLE_LIMITS.columns} data-testid={`template-table-${index}-add-column`}
          onClick={addColumn}><Plus size={13} className="mr-1" />Colonne</Button>
      </div>
    </div>
  );
}

function PairsEditor({ block, index, onChange, onCaret }) {
  const pairs = block.pairs || [];
  return (
    <div className="space-y-2" data-testid={`template-pairs-${index}`}>
      {pairs.map((_, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_auto] items-center gap-2">
          <Field block={block} index={index} path={["pairs", i, "label"]} onChange={onChange} onCaret={onCaret} max={200} label={`Libellé ${i + 1}`}
            placeholder="Libellé" className="h-9" testid={`template-pair-${index}-label-${i}`} />
          <Field block={block} index={index} path={["pairs", i, "value"]} onChange={onChange} onCaret={onCaret} max={500} label={`Valeur ${i + 1}`}
            placeholder="Valeur ou variable" className="h-9" testid={`template-pair-${index}-value-${i}`} />
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-red-600" aria-label={`Retirer la ligne ${i + 1}`}
            disabled={pairs.length <= 1} onClick={() => onChange({ ...block, pairs: pairs.filter((_, j) => j !== i) })}><Trash2 size={13} /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" disabled={pairs.length >= TABLE_LIMITS.pairs} data-testid={`template-pairs-${index}-add`}
        onClick={() => onChange({ ...block, pairs: [...pairs, { label: "", value: "" }] })}><Plus size={13} className="mr-1" />Ligne</Button>
    </div>
  );
}

// The image of a block: one of the media library's images (never an address, never an upload from here — D-84).
function ImagePicker({ block, index, assets, onChange }) {
  const usable = assets.filter((a) => !a.retired || a.id === block.asset_id);
  if (!usable.length) {
    return <p className="text-sm text-slate-500" data-testid={`template-image-${index}-empty`}>Aucune image dans la médiathèque. Ajoutez un logo ou un cachet depuis la charte graphique.</p>;
  }
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Image" data-testid={`template-image-${index}`}>
      {usable.map((a) => (
        <button key={a.id} type="button" role="radio" aria-checked={block.asset_id === a.id} onClick={() => onChange({ ...block, asset_id: a.id })}
          data-testid={`template-image-${index}-${a.id}`}
          className={`flex w-28 flex-col items-center gap-1 rounded-lg border p-2 text-xs ${block.asset_id === a.id ? "border-jamora ring-1 ring-jamora" : "border-slate-200 hover:bg-slate-50"}`}>
          {a.thumb ? <img src={a.thumb} alt="" className="h-14 w-full object-contain" /> : <span className="h-14" />}
          <span className="w-full truncate text-center text-slate-700">{a.name}</span>
          <span className="text-slate-400">{a.kind === "stamp" ? "Cachet" : "Logo"}{a.retired ? " · retirée" : ""}</span>
        </button>
      ))}
    </div>
  );
}

export function BlockEditor({ block, index, count, assets, variables, onPickVariable, clauseStatus, onChange, onMove, onRemove, onCaret, onClauseUpdate, onClauseDetach }) {
  const label = BLOCK_KINDS[block.kind];
  const field = (path, extra = {}) => <Field block={block} index={index} path={path} onChange={onChange} onCaret={onCaret} {...extra} />;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid={`template-block-${index}`}>
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex-1">{label}</span>
        {variables && onPickVariable && canInsertVariable(block) && <VariablePicker variables={variables} onPick={onPickVariable} testid={`template-variables-${index}`} />}
        <OptionsButton block={block} index={index} onChange={onChange} />
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Monter le bloc ${index + 1}`} disabled={index === 0} onClick={() => onMove(-1)}><ArrowUp size={14} /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Descendre le bloc ${index + 1}`} disabled={index === count - 1} onClick={() => onMove(1)}><ArrowDown size={14} /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-red-600" aria-label={`Retirer le bloc ${index + 1}`} onClick={onRemove}><Trash2 size={14} /></Button>
      </div>
      {TEXT_BLOCKS.includes(block.kind) && (
        block.kind === "paragraph"
          ? field(["text"], { multiline: true, rows: 4, placeholder: PLACEHOLDERS.paragraph, label: `${label} ${index + 1}`, testid: `template-block-text-${index}` })
          : field(["text"], { placeholder: PLACEHOLDERS[block.kind], label: `${label} ${index + 1}`, testid: `template-block-text-${index}` })
      )}
      {block.kind === "list" && field(["items"], { multiline: true, rows: 3, placeholder: "Un élément par ligne", label: `Liste ${index + 1}`, max: 50000, testid: `template-block-text-${index}` })}
      {block.kind === "table" && <TableEditor block={block} index={index} onChange={onChange} onCaret={onCaret} />}
      {block.kind === "keyvalue" && <PairsEditor block={block} index={index} onChange={onChange} onCaret={onCaret} />}
      {block.kind === "callout" && (
        <div className="space-y-2">
          {field(["title"], { max: 200, placeholder: "Titre (facultatif)", label: `Titre de l'encadré ${index + 1}`, testid: `template-block-title-${index}` })}
          {field(["text"], { multiline: true, rows: 3, placeholder: "Texte de l'encadré…", label: `Texte de l'encadré ${index + 1}`, testid: `template-block-text-${index}` })}
        </div>
      )}
      {block.kind === "amount" && (
        <div className="grid gap-2 sm:grid-cols-2">
          {field(["label"], { max: 200, placeholder: "Libellé (ex. Prix mensuel)", label: `Libellé du montant ${index + 1}`, testid: `template-block-label-${index}` })}
          {field(["value"], { max: 200, placeholder: "Valeur (ex. {abonnement.prix})", label: `Valeur du montant ${index + 1}`, testid: `template-block-value-${index}` })}
        </div>
      )}
      {block.kind === "image" && <ImagePicker block={block} index={index} assets={assets} onChange={onChange} />}
      {block.kind === "two_columns" && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid={`template-columns-${index}`}>
          {["left", "right"].map((side) => (
            <div key={side} className="space-y-2">
              {field([`${side}_title`], { max: 200, placeholder: side === "left" ? "Titre de gauche (facultatif)" : "Titre de droite (facultatif)",
                label: `Titre de la colonne ${side === "left" ? "de gauche" : "de droite"}`, testid: `template-columns-${index}-${side}-title` })}
              {field([`${side}_text`], { multiline: true, rows: 4, max: 3000, placeholder: "Texte…", label: `Texte de la colonne ${side === "left" ? "de gauche" : "de droite"}`,
                testid: `template-columns-${index}-${side}-text` })}
            </div>
          ))}
        </div>
      )}
      {block.kind === "parties" && (
        <p className="text-xs text-slate-500" data-testid={`template-parties-${index}`}>
          Identifie la société (nom, adresse, représentant légal) et la pharmacie (nom, adresse, pharmacien titulaire), avec leurs valeurs au moment de la génération.
          La génération est refusée tant qu'une de ces valeurs manque ; la forme juridique, les immatriculations, la commune et la région ne sont affichées que si elles sont connues.
        </p>
      )}
      {block.kind === "subscription_summary" && (
        <p className="text-xs text-slate-500" data-testid={`template-summary-${index}`}>
          Offre, numéro, prix, périodicité et date de début de l'abonnement choisi à la génération. La génération est refusée sans abonnement.
        </p>
      )}
      {block.kind === "clause" && <ClauseCard block={block} index={index} status={clauseStatus} onUpdate={onClauseUpdate} onDetach={onClauseDetach} />}
      {block.kind === "separator" && <p className="text-xs text-slate-500">Un filet ou un espace entre deux blocs ; le style se règle dans « Options ».</p>}
      {block.kind === "page_break" && <p className="text-xs text-slate-500">La suite commence sur une nouvelle page.</p>}
      {block.kind === "signatures" && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">« Pour {"{societe.nom}"} » et « Pour {"{pharmacie.nom}"} »{optionsOf(block).signers === 3 ? " et une troisième partie" : ""}, côte à côte, avec la place de signer.</p>
          {optionsOf(block).signers === 3 && field(["third_label"], { max: 60, placeholder: "Troisième signataire (ex. Le témoin)", label: `Troisième signataire ${index + 1}`, testid: `template-block-third-${index}` })}
        </div>
      )}
    </div>
  );
}

export function VariablePicker({ variables, onPick, disabled, testid = "template-variables" }) {
  const [open, setOpen] = useState(false);
  return (
    <ResponsivePopover open={open} onOpenChange={setOpen}>
      <ResponsivePopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8" disabled={disabled} data-testid={testid}><Braces size={14} className="mr-1.5" /><span className="sm:hidden" aria-hidden="true">Variable</span><span className="sr-only sm:not-sr-only">Insérer une variable</span></Button>
      </ResponsivePopoverTrigger>
      <ResponsivePopoverContent title="Insérer une variable" align="start" className="w-80 max-h-96 overflow-y-auto p-2">
        {groupVariables(variables).map((g) => (
          <div key={g.label} className="py-1">
            <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{g.label}</p>
            {g.items.map((v) => (
              <button key={v.key} type="button" onClick={() => { onPick(v.key); setOpen(false); }} data-testid={`template-variable-${v.key}`}
                className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50">
                <span>{v.label}</span><code className="text-xs text-slate-500">{`{${v.key}}`}</code>
              </button>
            ))}
          </div>
        ))}
      </ResponsivePopoverContent>
    </ResponsivePopover>
  );
}

// « Mise en page » of the template: header, footer, cover page, table of contents and watermark. Frozen with the version when it is published.
export function LayoutCard({ layout, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const watermark = WATERMARK_CHOICES.find(([value]) => value === layout.watermark.mode)?.[1] || "Aucun";
  const summary = [layout.cover.enabled && "Page de garde", layout.header.enabled && "En-tête", layout.footer.enabled && "Pied de page",
    layout.toc.enabled && "Sommaire", layout.watermark.mode !== "none" && `Filigrane ${watermark.toLowerCase()}`].filter(Boolean).join(" · ") || "Aucun élément de mise en page";
  const toggle = (part, enabled) => onChange({ ...layout, [part]: { enabled } });
  const rows = [
    ["cover", "Page de garde", "Une première page avec le titre, le logo, les parties et la date, sans en-tête ni pied de page. Le premier titre du modèle y est repris."],
    ["header", "En-tête", "Le logo de la charte à gauche ; à droite, l'identité de la société sur la première page, puis le logo seul."],
    ["footer", "Pied de page", "Le nom et l'adresse de la société, avant la numérotation des pages."],
    ["toc", "Sommaire", "La liste des sections et des articles, avec leur page dans le PDF."],
  ];
  return (
    <Card className="p-0" data-testid="template-layout">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="template-layout-toggle"
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left">
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-slate-900">Mise en page</span>
          <span className="block truncate text-xs text-slate-500" data-testid="template-layout-summary">{summary}</span>
        </span>
        <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-3 border-t border-slate-100 px-4 py-3">
          {rows.map(([part, title, hint]) => (
            <div key={part} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor={`layout-${part}`} className="text-sm">{title}</Label>
                <p className="text-xs text-slate-500">{hint}</p>
              </div>
              <Switch id={`layout-${part}`} checked={layout[part].enabled} disabled={disabled} onCheckedChange={(v) => toggle(part, v)} data-testid={`template-layout-${part}`} />
            </div>
          ))}
          <div className="space-y-1.5">
            <p className="text-sm font-medium text-slate-900">Filigrane</p>
            <p className="text-xs text-slate-500">Un mot en grand, en filigrane, sur chaque page du PDF.</p>
            <Segmented value={layout.watermark.mode} choices={WATERMARK_CHOICES} label="Filigrane" testid="template-layout-watermark"
              onChange={(mode) => onChange({ ...layout, watermark: { mode } })} />
          </div>
          <p className="text-xs text-slate-500">L'identité de la société se saisit dans Paramètres › Documents ; le logo, dans la charte graphique.</p>
        </div>
      )}
    </Card>
  );
}

// The list of blocks one can add, grouped by family — the same menu under the last block (« Ajouter un bloc ») and between two blocks (« + »).
export function AddBlockMenu({ groups, onAdd, testid, itemPrefix, inline = false, label = "Ajouter un bloc" }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {inline ? (
          <button type="button" aria-label={label} title={label} data-testid={testid}
            className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 transition hover:border-jamora hover:bg-jamora hover:text-white focus-visible:border-jamora focus-visible:text-jamora data-[state=open]:border-jamora data-[state=open]:bg-jamora data-[state=open]:text-white">
            <Plus size={14} aria-hidden="true" />
          </button>
        ) : (
          <Button variant="outline" size="sm" data-testid={testid}><Plus size={14} className="mr-1.5" />{label}</Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" title="Ajouter un bloc">
        {groups.map((group, i) => (
          <div key={group.label}>
            {i > 0 && <DropdownMenuSeparator />}
            <p className="px-2 pb-1 pt-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{group.label}</p>
            {group.kinds.map((kind) => (
              <DropdownMenuItem key={kind} onClick={() => onAdd(kind)} data-testid={`${itemPrefix}-${kind}`}>{BLOCK_KINDS[kind]}</DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// The line between two blocks with a centred « + »: a solid rule on each side of the button, half the gap above and half below, so the line is
// exactly as far from the block before as from the block after.
export function BlockInserter({ groups, onAdd, index, itemPrefix }) {
  return (
    <div className="flex h-9 items-center gap-2" data-testid={`insert-block-${index}`}>
      <span className="h-px flex-1 bg-slate-300" aria-hidden="true" />
      <AddBlockMenu groups={groups} onAdd={onAdd} itemPrefix={itemPrefix} inline testid={`insert-block-${index}-button`} label={`Ajouter un bloc à la place ${index + 1}`} />
      <span className="h-px flex-1 bg-slate-300" aria-hidden="true" />
    </div>
  );
}

// A labelled rule in the list of blocks: « Page de garde » above what goes on the cover, « Contenu » above the rest.
export function SectionBand({ label, hint, testid }) {
  return (
    <div className="flex items-center gap-3 pb-1 pt-3" data-testid={testid}>
      <span className="text-xs font-semibold uppercase tracking-wide text-jamora">{label}</span>
      <span className="h-px flex-1 bg-jamora/30" aria-hidden="true" />
      {hint && <span className="text-xs text-slate-500">{hint}</span>}
    </div>
  );
}
